import React, { useEffect, useMemo, useRef } from 'react';
import gql from 'graphql-tag';
import { useMutation, useQuery } from '@apollo/client';

import { useAdyen, useCurrency, useI18n, useLogger, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { PrintRow } from '~Components/PrinterManager';

import { MethodProps } from '../types';
import {
  AdyenPaymentFromBackend,
  AdyenPaymentFromBackendVariables,
  CreateKlarnaPaymentLink,
  CreateKlarnaPaymentLinkVariables,
  ExpireKlarnaPaymentLink,
  ExpireKlarnaPaymentLinkVariables,
  KlarnaPaymentLinkStatus,
  KlarnaPaymentLinkStatusVariables,
} from './__queries__';

/**
 * Creates the Adyen Pay by Link link the shopper pays with.
 *
 * Adyen's Terminal API has no Klarna support - Klarna appears neither in the
 * terminals' payment methods nor in the QR code wallets they can render - so
 * there is no way to run this through the terminal the way we run cards. Pay by
 * Link is the route Adyen (and Klarna) document for in-person Klarna: we create
 * a link, the shopper scans it, and they pay on their own phone.
 */
const CREATE_PAYMENT_LINK = gql`
  mutation CreateKlarnaPaymentLink($input: CreateAdyenPaymentLinkInput!) {
    createAdyenPaymentLink(input: $input) {
      success
      linkId
      url
      paymentId
      expiresAt
      message
      errorCode
      error
    }
  }
`;

/**
 * Kills the link when staff cancel. The QR is by then on the terminal display
 * and possibly on a printed slip, so without this the shopper could still pay
 * an order we have already torn down.
 */
const EXPIRE_PAYMENT_LINK = gql`
  mutation ExpireKlarnaPaymentLink($venueId: String!, $linkId: String!) {
    expireAdyenPaymentLink(venueId: $venueId, linkId: $linkId) {
      success
    }
  }
`;

/**
 * The link's own status. The AUTHORISATION webhook tells us about success, but
 * never about the shopper simply walking away, so we watch for `expired` here.
 */
const PAYMENT_LINK_STATUS = gql`
  query KlarnaPaymentLinkStatus($venueId: String!, $linkId: String!) {
    AdyenPaymentLink(venueId: $venueId, linkId: $linkId) {
      status
      expiresAt
      message
      errorCode
    }
  }
`;

/**
 * The same query Adyen.tsx polls. Our backend pre-creates the payment row with
 * the ID it hands back as `paymentId`, and the AUTHORISATION webhook fills in
 * the pspReference, so this appearing means the money is actually in.
 */
const BACKEND_ADYEN_PAYMENT = gql`
  query AdyenPaymentFromBackend($merchantReference: String!, $venueId: ID!) {
    AdyenPayment(id: $merchantReference, venueId: $venueId) {
      id
      method
      methodIdentifier
      createdAt
      pspReference
      tip
    }
  }
`;

/**
 * OM's language codes are not Adyen shopper locales, so map them. Anything
 * unmapped falls through to Adyen's own default (the shopper's browser).
 *
 * {new-country-onboarding} Add new languages here.
 */
const LANGUAGE_TO_SHOPPER_LOCALE: Record<string, string> = {
  se: 'sv-SE',
  en: 'en-US',
  es: 'es-ES',
  no: 'nb-NO',
  dk: 'da-DK',
  tr: 'tr-TR',
};

/** How often we ask the backend whether the payment landed. */
const PAYMENT_POLL_INTERVAL = 2000;

/**
 * How often we check the link itself. Slower than the payment poll because it
 * is only there to catch expiry, and every call is a round trip to Adyen.
 */
const LINK_POLL_INTERVAL = 5000;

const Klarna: React.FunctionComponent<MethodProps> = ({
  payment,
  order,
  amount,
  onComplete,
  onFail,
  onMetaChange,
  venueId,
}) => {
  const logger = useLogger('payment:klarna');
  const adyen = useAdyen();
  const currency = useCurrency();
  const { i18n } = useI18n();
  const [storage] = useStorage();

  const country = useSelector((state) => state.country);
  const companyId = useSelector((state) => state.company);
  const language = useSelector((state) => state.language);

  const { paymentId, paymentLinkId } = payment.meta;

  /**
   * Set once the payment is done (either way), so the pollers and the cancel
   * effect stop acting on a payment whose lifecycle is already over. Same guard
   * Adyen.tsx uses.
   */
  const lifecycleOver = useRef(false);

  /** Guards against the creation effect running twice. */
  const hasCreatedLink = useRef(false);

  const terminalId = payment.variant || storage.adyenTerminals[0]?.POIID;

  const [createPaymentLink] = useMutation<
    CreateKlarnaPaymentLink,
    CreateKlarnaPaymentLinkVariables
  >(CREATE_PAYMENT_LINK, { fetchPolicy: 'no-cache' });

  const [expirePaymentLink] = useMutation<
    ExpireKlarnaPaymentLink,
    ExpireKlarnaPaymentLinkVariables
  >(EXPIRE_PAYMENT_LINK, { fetchPolicy: 'no-cache' });

  /**
   * Klarna approves the payment only if the line items add up to exactly the
   * amount charged, and it reads amountIncludingTax as a UNIT price, totalling
   * each line as (unit price x quantity).
   *
   * Our cart stores a line TOTAL for `count` units, so deriving a unit price
   * means dividing in minor units, which rounds - and a rounded öre is enough
   * for Klarna to decline the whole payment. So we send one line per cart row
   * with quantity 1 and the line total as the price, and put the count in the
   * description instead. The invoice reads the same and the sum always
   * reconciles.
   *
   * For a split payment the cart is the whole order but we are only charging
   * part of it, so the rows cannot reconcile by definition. There we fall back
   * to a single synthetic line for the amount actually being charged.
   */
  const lineItems = useMemo(() => {
    const items = (order?.items ?? []).filter((item) => !item.isMessage);

    const rows = items.map((item, index) => ({
      id: item.id || `item-${index}`,
      description: item.count > 1 ? `${item.count} x ${item.title}` : item.title,
      quantity: 1,
      amountIncludingTax: item.amount - item.discount,
      vatRate: Math.round(item.vatRate),
    }));

    const rowTotal = rows.reduce((sum, row) => sum + row.amountIncludingTax, 0);

    if (rowTotal === amount) {
      return rows;
    }

    logger.info('Cart rows do not match the charged amount, sending a single line item', {
      rowTotal,
      charged: amount,
    });

    return [
      {
        id: order?.id ?? payment.id,
        description: order?.orderNumber
          ? `${i18n('Klarna.LineItemOrder')} ${order.orderNumber}`
          : i18n('Klarna.LineItemOrder'),
        quantity: 1,
        amountIncludingTax: amount!,
        // Mixed VAT rates across the order, so we cannot state one. Klarna
        // does not require it, and the venue's own receipt remains the tax
        // document.
        vatRate: undefined,
      },
    ];
  }, [order?.items, amount]);

  /**
   * Create the link, then get the QR in front of the shopper: on the terminal's
   * customer display (which faces them, unlike the POS screen) and on a printed
   * slip they can take away while they finish paying.
   */
  useEffect(() => {
    if (hasCreatedLink.current || !amount) {
      return;
    }

    hasCreatedLink.current = true;

    const run = async () => {
      logger.info('Creating Klarna payment link', {
        amount,
        orderId: order?.id,
        lineItemCount: lineItems.length,
      });

      const { data } = await createPaymentLink({
        variables: {
          input: {
            venueId,
            companyId,
            amount: amount!,
            currency: currency.code,
            // Null for companies created before the field existed. The backend
            // prefers the legal entity's country anyway and falls back to SE.
            countryCode: country ?? undefined,
            shopperLocale: LANGUAGE_TO_SHOPPER_LOCALE[language],
            description: order?.orderNumber
              ? `${i18n('Klarna.LineItemOrder')} ${order.orderNumber}`
              : undefined,
            lineItems,
          },
        },
      });

      const result = data?.createAdyenPaymentLink;

      if (!result?.success || !result.url) {
        // Adyen says useful things here - "Line items are required for the
        // selected payment method", "Klarna is not enabled on this account" -
        // so log all of it. Staff get the translated line; Datadog gets the
        // error code and raw body, which is what actually gets this diagnosed.
        logger.error(
          `Failed to create Klarna payment link: ${result?.message ?? 'no reason given'}`,
          {
            orderId: order?.id,
            amount,
            adyenErrorCode: result?.errorCode,
            adyenResponse: result?.error,
          },
        );

        onFail(
          result?.message
            ? `${i18n('Klarna.CouldNotCreateLink')}: ${result.message}`
            : i18n('Klarna.CouldNotCreateLink'),
        );
        return;
      }

      logger.info('Created Klarna payment link', {
        linkId: result.linkId,
        backendPaymentId: result.paymentId,
        expires: result.expiresAt,
      });

      // meta.qr makes PaymentStatus render the QR (and the cancel button) on
      // the POS, and meta.paymentId is what we poll for completion.
      onMetaChange({
        qr: result.url,
        paymentId: result.paymentId!,
        paymentLinkId: result.linkId!,
        amount,
      });

      // The customer display faces the shopper, so this is the one that
      // matters. Best effort: a venue may have no terminal, or an older model.
      if (terminalId) {
        adyen
          .displayQR(terminalId, result.url!)
          .then(() => logger.info('Showed Klarna QR on terminal', { terminalId }))
          .catch((e) =>
            logger.warn(`Could not show Klarna QR on terminal: ${(e as Error).message}`, {
              terminalId,
            }),
          );
      } else {
        logger.info('No Adyen terminal to show the Klarna QR on, POS screen only');
      }

      // Also print it, so the shopper can step aside and finish paying without
      // holding up the counter.
      const message: PrintRow[] = [
        { content: '     Klarna', fontSize: 28, alignment: 'CENTER' },
        { content: ' ' },
        { content: ' ' + i18n('Klarna.ScanCode'), fontSize: 18, alignment: 'CENTER' },
        { content: ' ' },
        { content: result.url!, qr: true },
        { content: ' ' },
        {
          content: `    ${i18n('Klarna.ToPay')}: ${currency.format(amount!)}`,
          fontSize: 18,
          alignment: 'CENTER',
        },
      ];

      window.printerManager?.initiatePrint({
        data: message,
        printType: 'KLARNA',
      });
    };

    run().catch((error) => {
      logger.error(`Klarna payment link creation threw: ${(error as Error).message}`, {
        orderId: order?.id,
      });

      onFail((error as Error).message);
    });
  }, [amount]);

  /**
   * Completion. The backend pre-creates the payment row, so this only returns
   * something once the AUTHORISATION webhook has filled in the pspReference.
   */
  const backendQuery = useQuery<AdyenPaymentFromBackend, AdyenPaymentFromBackendVariables>(
    BACKEND_ADYEN_PAYMENT,
    {
      variables: {
        merchantReference: paymentId!,
        venueId,
      },
      pollInterval: PAYMENT_POLL_INTERVAL,
      skip: !paymentId,
      fetchPolicy: 'network-only',
    },
  );
  const backendPayment = backendQuery.data?.AdyenPayment;

  /** Link lifecycle, purely to notice expiry. */
  const linkQuery = useQuery<KlarnaPaymentLinkStatus, KlarnaPaymentLinkStatusVariables>(
    PAYMENT_LINK_STATUS,
    {
      variables: {
        venueId,
        linkId: paymentLinkId!,
      },
      pollInterval: LINK_POLL_INTERVAL,
      skip: !paymentLinkId,
      fetchPolicy: 'network-only',
    },
  );
  const link = linkQuery.data?.AdyenPaymentLink;
  const linkStatus = link?.status;

  /**
   * A failed status lookup does not fail the payment - we keep waiting for the
   * webhook - but it must not vanish silently either, or an account-level
   * problem looks identical to a shopper taking their time.
   */
  useEffect(() => {
    if (!link || link.status || !link.message) {
      return;
    }

    logger.warn(`Could not read Klarna payment link status: ${link.message}`, {
      linkId: paymentLinkId,
      adyenErrorCode: link.errorCode,
    });
  }, [link?.message]);

  useEffect(() => {
    if (!backendPayment || lifecycleOver.current) {
      return;
    }

    // pspReference is 'N/A' until the webhook lands, so its presence is what
    // tells us the payment actually happened rather than that we created a row.
    if (!backendPayment.pspReference || backendPayment.pspReference === 'N/A') {
      return;
    }

    lifecycleOver.current = true;

    logger.info('Klarna payment completed', {
      backendPaymentId: backendPayment.id,
      method: backendPayment.method,
    });

    if (terminalId) {
      adyen.displayPaymentResult(terminalId, true).catch(() => {
        // Cosmetic only - never fail a paid order over the customer display.
      });
    }

    onComplete(null, {
      transactionId: backendPayment.pspReference,
      tip: backendPayment.tip ?? undefined,
    });
  }, [backendPayment]);

  useEffect(() => {
    if (!linkStatus || lifecycleOver.current) {
      return;
    }

    if (linkStatus !== 'expired') {
      return;
    }

    lifecycleOver.current = true;

    logger.warn('Klarna payment link expired before the shopper paid', {
      linkId: paymentLinkId,
      orderId: order?.id,
    });

    if (terminalId) {
      adyen.displayPaymentResult(terminalId, false).catch(() => {
        // Cosmetic only.
      });
    }

    onFail(i18n('Klarna.LinkExpired'));
  }, [linkStatus]);

  /** Staff pressed cancel on the POS. */
  useEffect(() => {
    if (!payment.meta.cancelRequested || lifecycleOver.current) {
      return;
    }

    lifecycleOver.current = true;

    logger.info('Klarna payment cancelled by staff', {
      linkId: paymentLinkId,
      orderId: order?.id,
    });

    // Expire the link before failing the payment. If this call fails the link
    // stays payable until it times out on its own, which is why the expiry
    // window is short.
    if (paymentLinkId) {
      expirePaymentLink({
        variables: {
          venueId,
          linkId: paymentLinkId,
        },
      }).catch((e) =>
        logger.error(`Could not expire Klarna payment link: ${(e as Error).message}`, {
          linkId: paymentLinkId,
        }),
      );
    }

    if (terminalId) {
      adyen.displayPaymentResult(terminalId, false).catch(() => {
        // Cosmetic only.
      });
    }

    onFail(i18n('General.Cancel'));
  }, [payment.meta.cancelRequested]);

  return null;
};

export default Klarna;
